/**
 * Vorbello Accurate WebM Duration Fixer
 *
 * Surgical EBML Header Injector:
 * Injects or updates the EBML Duration element (0x4489) strictly inside
 * the Segment Info container (0x1549A966), without modifying, re-serializing,
 * or altering ANY audio/video Clusters.
 *
 * This guarantees:
 * 1. Windows Media Player & File Explorer detect exact duration and allow full scrubbing.
 * 2. VLC does NOT crash or abort after 2 seconds (avoids the fatal "Infinite EBML element
 *    Cluster inside finite parent" error caused by re-serializing clusters).
 * 3. QuickTime, Chrome, and browser players display accurate timeline and controls.
 */

function readVint(view: DataView, offset: number, maxLen: number = 8): { length: number; value: number; isUnknown: boolean } | null {
  if (offset >= view.byteLength) return null;
  const first = view.getUint8(offset);
  let len = 1;
  let mask = 0x80;
  while (len <= maxLen && !(first & mask)) {
    len++;
    mask >>= 1;
  }
  if (len > maxLen) return null;

  let val = first & (mask - 1);
  for (let i = 1; i < len; i++) {
    if (offset + i >= view.byteLength) return null;
    val = (val * 256) + view.getUint8(offset + i);
  }
  const isUnknown = val === (Math.pow(2, 7 * len) - 1);
  return { length: len, value: val, isUnknown };
}

function encodeVint(val: number, targetWidth?: number): Uint8Array {
  let minW = 1;
  if (val >= 0x7f) minW = 2;
  if (val >= 0x3fff) minW = 3;
  if (val >= 0x1fffff) minW = 4;
  if (val >= 0x0fffffff) minW = 8;
  const w = targetWidth && targetWidth >= minW ? targetWidth : minW;
  const buf = new Uint8Array(w);
  let v = val;
  for (let i = w - 1; i > 0; i--) {
    buf[i] = v & 0xff;
    v = Math.floor(v / 256);
  }
  const marker = 1 << (8 - w);
  buf[0] = marker | (v & (marker - 1));
  return buf;
}

function readEbmlId(view: DataView, offset: number): { id: number; length: number } | null {
  if (offset >= view.byteLength) return null;
  const first = view.getUint8(offset);
  let len = 1;
  let mask = 0x80;
  while (len <= 4 && !(first & mask)) {
    len++;
    mask >>= 1;
  }
  if (len > 4) return null;
  let id = 0;
  for (let i = 0; i < len; i++) {
    if (offset + i >= view.byteLength) return null;
    id = (id * 256) + view.getUint8(offset + i);
  }
  return { id, length: len };
}

export async function fixWebmDuration(blob: Blob, durationMs: number): Promise<Blob> {
  if (!blob || blob.size === 0 || durationMs <= 0) {
    return blob;
  }

  try {
    // The EBML header, Segment header, and Info container are always within the first 64 KB of the file.
    const headerScanSize = Math.min(blob.size, 65536);
    const headerSlice = blob.slice(0, headerScanSize);
    const arrayBuffer = await headerSlice.arrayBuffer();
    const view = new DataView(arrayBuffer);

    let offset = 0;

    // 1. Validate EBML Header (0x1A45DFA3)
    const ebmlId = readEbmlId(view, offset);
    if (!ebmlId || ebmlId.id !== 0x1A45DFA3) {
      console.warn('[WebMDurationFixer] Not a valid EBML file, returning original blob');
      return blob;
    }
    offset += ebmlId.length;
    const ebmlLen = readVint(view, offset);
    if (!ebmlLen) return blob;
    offset += ebmlLen.length + ebmlLen.value;

    // 2. Locate Segment (0x18538067)
    const segId = readEbmlId(view, offset);
    if (!segId || segId.id !== 0x18538067) {
      console.warn('[WebMDurationFixer] Segment element not found, returning original blob');
      return blob;
    }
    offset += segId.length;
    const segLen = readVint(view, offset);
    if (!segLen) return blob;
    offset += segLen.length;

    const segStart = offset;
    const segEnd = segLen.isUnknown ? headerScanSize : Math.min(segStart + segLen.value, headerScanSize);

    // 3. Scan Segment's immediate children for Info (0x1549A966)
    let infoOffset = -1;
    let infoLen: { length: number; value: number } | null = null;
    let infoLenOffset = -1;
    let pos = segStart;

    while (pos < segEnd && pos < headerScanSize - 12) {
      const childId = readEbmlId(view, pos);
      if (!childId) break;
      const lenOffset = pos + childId.length;
      const childLen = readVint(view, lenOffset);
      if (!childLen) break;

      const payloadOffset = lenOffset + childLen.length;

      if (childId.id === 0x1549A966) { // Segment Info
        infoOffset = pos;
        infoLenOffset = lenOffset;
        infoLen = childLen;
        break;
      }

      if (childId.id === 0x1F43B675) { // Hit Cluster before Info (should not happen in valid WebM)
        break;
      }

      if (childLen.isUnknown) break;
      pos = payloadOffset + childLen.value;
    }

    if (infoOffset === -1 || !infoLen) {
      console.warn('[WebMDurationFixer] Segment Info container not found, returning original blob');
      return blob;
    }

    const infoPayloadStart = infoLenOffset + infoLen.length;
    const infoPayloadEnd = infoPayloadStart + infoLen.value;

    if (infoPayloadEnd > headerScanSize) {
      console.warn('[WebMDurationFixer] Info payload exceeds header scan boundary');
      return blob;
    }

    // 4. Scan inside Info for Duration (0x4489)
    let durationOffset = -1;
    let durationLen: { length: number; value: number } | null = null;
    let durationLenOffset = -1;
    let infoPos = infoPayloadStart;

    while (infoPos < infoPayloadEnd) {
      const elemId = readEbmlId(view, infoPos);
      if (!elemId) break;
      const lenOff = infoPos + elemId.length;
      const elemLen = readVint(view, lenOff);
      if (!elemLen) break;
      const dataOff = lenOff + elemLen.length;

      if (elemId.id === 0x4489) { // Duration element
        durationOffset = infoPos;
        durationLenOffset = lenOff;
        durationLen = elemLen;
        break;
      }
      infoPos = dataOff + elemLen.value;
    }

    if (durationOffset !== -1 && durationLen) {
      // In-place update: write Float64 / Float32 into existing duration bytes
      const dataOffset = durationLenOffset + durationLen.length;
      const modifiedHeader = new Uint8Array(arrayBuffer);
      const modView = new DataView(modifiedHeader.buffer);

      if (durationLen.value === 4) {
        modView.setFloat32(dataOffset, durationMs, false);
      } else if (durationLen.value === 8) {
        modView.setFloat64(dataOffset, durationMs, false);
      }

      console.info(`[WebMDurationFixer] In-place updated Duration: ${durationMs}ms at offset ${dataOffset}`);
      return new Blob([modifiedHeader as BlobPart, blob.slice(headerScanSize)], { type: blob.type || 'video/webm' });
    }

    // 5. Duration does not exist in Info: Construct surgical Duration element (11 bytes)
    const durValBuf = new Uint8Array(8);
    const durView = new DataView(durValBuf.buffer);
    durView.setFloat64(0, durationMs, false);

    const durElem = new Uint8Array(11);
    durElem[0] = 0x44; // Duration ID byte 1
    durElem[1] = 0x89; // Duration ID byte 2
    durElem[2] = 0x88; // VINT length 8 (0x80 | 8)
    durElem.set(durValBuf, 3);

    // Calculate new Info VINT length
    const newInfoSize = infoLen.value + durElem.length;
    const newInfoVint = encodeVint(newInfoSize, infoLen.length);

    // Assemble new blob cleanly:
    // [0 ... infoLenOffset] + [newInfoVint] + [infoPayloadStart ... infoPayloadEnd] + [durElem] + [infoPayloadEnd ... EOF]
    const part1 = blob.slice(0, infoLenOffset);
    const part2 = blob.slice(infoPayloadStart, infoPayloadEnd);
    const part3 = blob.slice(infoPayloadEnd);

    const fixedBlob = new Blob([part1, newInfoVint as BlobPart, part2, durElem as BlobPart, part3], { type: blob.type || 'video/webm' });
    console.info(`[WebMDurationFixer] Surgically injected Duration element (${durationMs}ms, +11 bytes). Total size: ${fixedBlob.size}`);
    return fixedBlob;
  } catch (err) {
    console.warn('[WebMDurationFixer] Error during surgical duration injection, falling back to original blob:', err);
    return blob;
  }
}

export default fixWebmDuration;
